import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import { useToast } from '../context/ToastContext';

const SearchEmployees = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchTerm.trim()) {
      showToast(t('searchEmployees.enterSearchTerm'), 'error');
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      const response = await api.get('/search/employees', {
        params: { q: searchTerm },
      });
      setEmployees(response.data);
    } catch (err) {
      console.error(err);
      showToast(t('searchEmployees.searchError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t('searchEmployees.title')} subtitle={t('searchEmployees.subtitle')} />

      {/* Search Form */}
      <form onSubmit={handleSearch}>
        <Card>
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('searchEmployees.placeholder')}
                className="input pl-10"
              />
            </div>
            <Button type="submit" variant="primary" size="md" loading={loading}>
              {loading ? t('searchEmployees.searching') : t('common.search')}
            </Button>
          </div>
        </Card>
      </form>

      {/* Results */}
      {searched && (
        <Card>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
            {t('searchEmployees.resultsTitle')} ({employees.length})
          </h2>

          {loading ? (
            <Spinner label={t('searchEmployees.searching')} />
          ) : employees.length === 0 ? (
            <EmptyState
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              }
              title={t('searchEmployees.noEmployeeFound')}
              description={t('searchEmployees.tryOtherTerms')}
            />
          ) : (
            <div className="space-y-3">
              {employees.map((emp) => (
                <div
                  key={emp.id}
                  className="border-2 border-slate-200 dark:border-slate-800 rounded-lg p-4 hover:border-primary-500 hover:bg-primary-50/50 dark:hover:bg-slate-800 transition"
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-lg">
                        {emp.user.firstName} {emp.user.lastName}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{emp.user.email}</p>
                      {emp.jobTitle && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1.5">
                          <svg
                            className="w-4 h-4 text-slate-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                          {emp.jobTitle}
                        </p>
                      )}
                      {emp.homeAddress && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                          <svg
                            className="w-4 h-4 text-slate-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          {emp.homeAddress}
                        </p>
                      )}
                    </div>

                    <div className="text-right flex-shrink-0">
                      <div className="flex gap-2 mb-2">
                        <Badge color="green">
                          <svg
                            className="w-3 h-3 inline mr-0.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                          {emp.stats.completed} {t('searchEmployees.completed')}
                        </Badge>
                        <Badge color="yellow">
                          <svg
                            className="w-3 h-3 inline mr-0.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {emp.stats.inProgress} {t('searchEmployees.inProgress')}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {t('searchEmployees.ofAssigned', { count: emp.stats.assigned })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default SearchEmployees;
